import { type FC } from "react";
import { z } from "zod/v4";
import { useUnistyles } from "react-native-unistyles";
import { View, Text } from "@visioni/a3s-ui/components";
import type { ActivityItemViewModel } from "../ActivityItemCard/ActivityItemCard";

// ---------------------------------------------------------------------------
// Schemas — local parse-only schemas matching what the recorder stores.
// Kept loose with .passthrough() so unexpected fields don't cause failures.
// ---------------------------------------------------------------------------

const AlarmResponseSchema = z
  .object({
    monitoring: z.string(),
    recording: z.string(),
    sirens: z.string(),
    strobes: z.string(),
  })
  .loose();
type AlarmResponseData = z.infer<typeof AlarmResponseSchema>;

const AlarmRoutineWindowSchema = z
  .object({
    name: z.string().optional(),
    days: z.array(z.string()),
    timeRanges: z.array(z.record(z.string(), z.unknown())),
  })
  .loose();

const AlarmRoutineSchema = z
  .object({
    kind: z.string(),
    windows: z.array(AlarmRoutineWindowSchema).optional(),
    start: z.unknown().optional(),
    pattern: z.array(z.unknown()).optional(),
  })
  .loose();
type AlarmRoutineData = z.infer<typeof AlarmRoutineSchema>;

const ExceptionPeriodPartSchema = z.object({
  year: z.number(),
  month: z.number(),
  day: z.number(),
  hour: z.number(),
  minute: z.number(),
});

const AlarmOverrideSchema = z
  .object({
    id: z.string().optional(),
    reason: z.string().optional(),
    action: z.string().optional(),
    author: z.string().nullable().optional(),
    period: z
      .object({
        start: ExceptionPeriodPartSchema.optional(),
        end: ExceptionPeriodPartSchema.nullable().optional(),
      })
      .optional(),
  })
  .loose();
type AlarmOverrideData = z.infer<typeof AlarmOverrideSchema>;

const AlarmExceptionSchema = z
  .object({
    id: z.string().optional(),
    action: z.string(),
    reason: z.string().optional(),
    author: z.string().nullable().optional(),
    period: z.object({
      start: ExceptionPeriodPartSchema,
      end: ExceptionPeriodPartSchema,
    }),
  })
  .loose();
type AlarmExceptionData = z.infer<typeof AlarmExceptionSchema>;

const ExceptionsDataSchema = z.object({
  exceptions: z.array(AlarmExceptionSchema),
  exceptionsCount: z.number().optional(),
});

const ResponseDataSchema = z.object({ response: AlarmResponseSchema });
const RoutineDataSchema = z.object({ routine: AlarmRoutineSchema.nullable() });
const OverrideDataSchema = z.object({ override: AlarmOverrideSchema.nullable() });
const TimezoneDataSchema = z.object({ timezone: z.string() });

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function capitalise(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function formatDays(days: string[]): string {
  if (days.length === 0) return "";
  if (days.length === 7) return "Every day";
  if (days.length === 5 && !days.includes("SAT") && !days.includes("SUN")) return "Weekdays";
  if (days.length === 2 && days.includes("SAT") && days.includes("SUN")) return "Weekends";
  return days.map((d) => capitalise(d.toLowerCase())).join(", ");
}

function pad2(n: unknown): string {
  return String(Number(n) || 0).padStart(2, "0");
}

function formatTimeRange(range: Record<string, unknown>): string {
  if (range["kind"] === "all_day") return "All day";
  const start = range["start"];
  const end = range["end"];
  if (start && end && typeof start === "object" && typeof end === "object") {
    const s = start as Record<string, unknown>;
    const e = end as Record<string, unknown>;
    return `${pad2(s["hour"])}:${pad2(s["minute"])} - ${pad2(e["hour"])}:${pad2(e["minute"])}`;
  }
  return "Custom range";
}

function formatPeriodPart(part: z.infer<typeof ExceptionPeriodPartSchema>): string {
  const date = new Date(part.year, part.month - 1, part.day, part.hour, part.minute);
  return date.toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

// ---------------------------------------------------------------------------
// Sub-renderers
// ---------------------------------------------------------------------------

const FieldRow: FC<{ readonly label: string; readonly value: string }> = ({ label, value }) => (
  <View flexDirection="row" alignItems="center" gap="md">
    <Text textStyle="xxs/medium" color="textMuted" style={{ width: 100 }}>
      {label}
    </Text>
    <Text textStyle="xxs/regular" color="text">
      {value}
    </Text>
  </View>
);

const ResponseDetails: FC<{ readonly response: AlarmResponseData }> = ({ response }) => (
  <View gap="xs">
    <Text textStyle="xxs/medium" color="text" style={{ marginBottom: 4 }}>
      Features configured:
    </Text>
    <View px="md" py="xs" bg="muted" borderRadius={6} gap="xs">
      {Object.entries(response)
        .filter(([feature]) => feature === "strobes" || feature === "sirens")
        .map(([feature, state]) => (
          <FieldRow key={feature} label={capitalise(feature)} value={String(state).toUpperCase()} />
        ))}
    </View>
  </View>
);

const RoutineDetails: FC<{ readonly routine: AlarmRoutineData }> = ({ routine }) => {
  const { theme } = useUnistyles();

  if (routine.kind === "weekly" && (!routine.windows || routine.windows.length === 0)) {
    return (
      <View gap="xs">
        <FieldRow label="Type" value="Weekly" />
        <Text textStyle="xxs/regular" color="textMuted">
          Always disarmed (no armed windows configured)
        </Text>
      </View>
    );
  }

  return (
    <View gap="xs">
      <FieldRow label="Type" value={capitalise(routine.kind)} />
      {routine.windows && routine.windows.length > 0 && (
        <View gap="sm" style={{ marginTop: theme.spacing.sm }}>
          <Text textStyle="xxs/medium" color="text">
            Armed windows:
          </Text>
          <View flexDirection="row" flexWrap="wrap" gap="sm">
            {routine.windows.map((window, idx) => {
              const daysLabel = formatDays(window.days);
              const showDays = !window.name || daysLabel.toLowerCase() !== window.name.toLowerCase();
              return (
                <View
                  key={window.name ?? String(idx)}
                  px="md"
                  py="xs"
                  bg="muted"
                  borderRadius={6}
                  gap="xs"
                  style={{ minWidth: 160 }}
                >
                  <Text textStyle="xxs/medium" color="text">
                    {window.name ?? daysLabel}
                  </Text>
                  {showDays && (
                    <Text textStyle="xxs/regular" color="textMuted">
                      {daysLabel}
                    </Text>
                  )}
                  {window.timeRanges.map((range, rIdx) => (
                    <Text key={String(rIdx)} textStyle="xxs/regular" color="textMuted">
                      {formatTimeRange(range)}
                    </Text>
                  ))}
                </View>
              );
            })}
          </View>
        </View>
      )}
      {routine.kind === "rotating" && routine.pattern && (
        <FieldRow label="Pattern" value={`${String(routine.pattern.length)} rotation(s)`} />
      )}
    </View>
  );
};

const OverrideDetails: FC<{ readonly override: AlarmOverrideData | null }> = ({ override }) => {
  if (!override) {
    return (
      <Text textStyle="xxs/regular" color="textMuted">
        Override removed (returned to routine schedule)
      </Text>
    );
  }
  return (
    <View gap="xs">
      {override.action && <FieldRow label="Action" value={capitalise(override.action)} />}
      {override.reason && <FieldRow label="Reason" value={override.reason} />}
      {override.period?.start && <FieldRow label="From" value={formatPeriodPart(override.period.start)} />}
      {override.period?.end && <FieldRow label="Until" value={formatPeriodPart(override.period.end)} />}
      {override.period && !override.period.end && <FieldRow label="Until" value="Indefinite" />}
    </View>
  );
};

const ExceptionDetails: FC<{ readonly exceptions: readonly AlarmExceptionData[] }> = ({ exceptions }) => {
  if (exceptions.length === 0) {
    return (
      <Text textStyle="xxs/regular" color="textMuted">
        All exceptions removed
      </Text>
    );
  }
  return (
    <View gap="sm">
      <Text textStyle="xxs/medium" color="text">
        {exceptions.length === 1 ? "1 exception" : `${String(exceptions.length)} exceptions`}
      </Text>
      {exceptions.map((exception, idx) => (
        <View key={exception.id ?? String(idx)} px="md" py="xs" bg="muted" borderRadius={6} gap="xs">
          <FieldRow label="State" value={capitalise(exception.action)} />
          <FieldRow
            label="Period"
            value={`${formatPeriodPart(exception.period.start)} – ${formatPeriodPart(exception.period.end)}`}
          />
          {exception.reason ?
            <FieldRow label="Reason" value={exception.reason} />
          : null}
        </View>
      ))}
    </View>
  );
};

const ParseError: FC = () => (
  <Text textStyle="xxs/regular" color="textMuted">
    Unable to display configuration details
  </Text>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export interface ActivityConfigDetailsProps {
  readonly item: ActivityItemViewModel;
}

export const ActivityConfigDetails: FC<ActivityConfigDetailsProps> = ({ item }) => {
  const { data, kind } = item;

  if (!data) {
    return (
      <Text textStyle="xxs/regular" color="textMuted">
        No configuration data available
      </Text>
    );
  }

  switch (kind) {
    case "alarm_response_configured": {
      const result = ResponseDataSchema.safeParse(data);
      if (!result.success) return <ParseError />;
      return <ResponseDetails response={result.data.response} />;
    }

    case "alarm_routine_configured": {
      const result = RoutineDataSchema.safeParse(data);
      if (!result.success) return <ParseError />;
      if (!result.data.routine) return <ParseError />;
      return <RoutineDetails routine={result.data.routine} />;
    }

    case "alarm_override_configured": {
      const result = OverrideDataSchema.safeParse(data);
      if (!result.success) return <ParseError />;
      return <OverrideDetails override={result.data.override} />;
    }

    case "alarm_exception_configured": {
      const result = ExceptionsDataSchema.safeParse(data);
      if (!result.success) return <ParseError />;
      return <ExceptionDetails exceptions={result.data.exceptions} />;
    }

    case "alarm_timezone_configured": {
      const result = TimezoneDataSchema.safeParse(data);
      if (!result.success) return <ParseError />;
      return <FieldRow label="Timezone" value={result.data.timezone} />;
    }

    default:
      return (
        <Text textStyle="xxs/regular" color="textMuted">
          {JSON.stringify(data, null, 2)}
        </Text>
      );
  }
};
